import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function VerifySuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const status = params.get("status") || "verified";
  const email = params.get("email") || "";
  const { setAuthOpen } = useAuth();

  const view = useMemo(() => {
    if (status === "verified") return {
      icon: <CheckCircle2 size={40} className="text-[#15803D]" />,
      title: "Email verified",
      body: <>Great — {email && <b className="text-foreground">{email}</b>} is confirmed. Log in to start using MyAE.</>,
      cta: { label: "Log in", onClick: () => { setAuthOpen({ open: true, mode: "login" }); navigate("/"); } },
    };
    if (status === "expired") return {
      icon: <Clock size={40} className="text-amber-500" />,
      title: "This link has expired",
      body: <>Verification links expire after 24 hours. Sign in and we&apos;ll send you a fresh one.</>,
      cta: { label: "Sign in", onClick: () => { setAuthOpen({ open: true, mode: "login" }); navigate("/"); } },
    };
    return {
      icon: <XCircle size={40} className="text-[#B91C1C]" />,
      title: "This link is no longer valid",
      body: <>It may have already been used or the account no longer exists. Please sign up or log in below.</>,
      cta: { label: "Back to MyAE", onClick: () => navigate("/") },
    };
  }, [status, email, navigate, setAuthOpen]);

  useEffect(() => { document.title = "Verify email — MyAE"; }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="verify-success-page">
      <div className="bg-card border border-border w-full max-w-md p-8 text-center">
        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-secondary flex items-center justify-center">{view.icon}</div>
        <h1 className="font-serif text-3xl tracking-tight mb-2">{view.title}</h1>
        <p className="text-sm text-muted-foreground leading-relaxed mb-6">{view.body}</p>
        <button onClick={view.cta.onClick} data-testid="verify-cta"
          className="w-full bg-oxford text-white py-3 text-sm font-medium hover:bg-[#2C5282] transition-colors">
          {view.cta.label}
        </button>
      </div>
    </div>
  );
}
